import { Bullet, Circle, Label, p100, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  DateAxis,
  LineSeries,
  SmoothedXLineSeries,
  StepLineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { getChartInterval } from "@/helpers/charts";
import { parseDateByMetricWithMach } from "@/helpers/dataMapping";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

export default class LineChart extends BaseChart {
  interval!: time.ITimeInterval;

  protected parseXAxisValue(raw: string | number, metric: string): Date {
    if (metric === "quarter" && typeof raw === "string") {
      const quarterMatch = raw.match(/^Q([1-4])\s+(\d{2,4})$/);
      if (quarterMatch) {
        const quarterIndex = Number(quarterMatch[1]) - 1;
        let year = Number(quarterMatch[2]);
        if (year < 100) {
          year += 2000;
        }
        return new Date(Date.UTC(year, quarterIndex * 3, 1, 12));
      }
    }

    return parseDateByMetricWithMach(raw, metric);
  }

  initChart() {
    this.root.container.children.clear();
    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.data = this.mapData(this.data);
    this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;

    this.createChart();
    this.createRenderers();
    this.createAxes();
    this.createCursor();

    const series = [this.settings.yAxisSecondary, this.settings.yAxisPrimary];
    series.forEach((name, index) => this.createLineSeries(name, index));
    this.showLegend(series, (this.chart as XYChart).series.values as LegendData);
  }

  protected mapData(data: ChartDataRecord[]): ChartDataRecord[] {
    if (!Array.isArray(data)) return [];

    const isMonth = this.settings.xAxis === "month";

    return data.map((el) => {
      const raw = el[this.settings.xAxis] as string | number;
      let parsed: Date;
      if (this.settings.xAxis === "day" && typeof el.timestamp === "number") {
        parsed = new Date(el.timestamp);
      } else {
        const metric =
          this.settings.xAxis === "quarter"
            ? "quarter"
            : this.settings.xAxis === "week"
            ? "week"
            : isMonth
            ? "month"
            : "day";
        parsed = this.parseXAxisValue(raw, metric);
      }

      const timestamp = parsed.getTime();
      if (isNaN(timestamp)) {
        console.warn(`Invalid timestamp for raw: ${raw}`);
      }

      return {
        timestamp,
        [this.settings.xAxis]: raw,
        [this.settings.yAxisPrimary]: el[this.settings.yAxisPrimary] ?? 0,
        [this.settings.yAxisSecondary]: el[this.settings.yAxisSecondary] ?? 0,
      };
    });
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
        focusable: true,
      })
    );

    if (this.settings.showDataLabels) {
      this.chart.setAll({
        paddingLeft: 30,
        paddingRight: 30,
      });
    }
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, { minGridDistance: 80 });
    x.labels.template.setAll({
      centerX: p50,
      ...chartFontSettings.label,
    });

    const yPrimary = AxisRendererY.new(this.root, {});
    yPrimary.grid.template.setAll({
      strokeWidth: 1,
      strokeDasharray: [10, 5],
    });
    yPrimary.labels.template.setAll({ ...chartFontSettings.label });

    const ySecondary = AxisRendererY.new(this.root, { opposite: true });
    ySecondary.labels.template.setAll({ ...chartFontSettings.label });

    this.renderers = { x, yPrimary, ySecondary };
  }

  protected createAxes() {
    const isMonth = this.settings.xAxis === "month";
    const dateFormats = getAxisDateFormat(this.settings.xAxis);
    const isPlanLabelsGranularity =
      this.module === PLAN_MODULE &&
      this.settings.showDataLabels &&
      (this.settings.xAxis === "week" || this.settings.xAxis === "month" || this.settings.xAxis === "quarter");
    const isNonPlanDayGranularity = this.module !== PLAN_MODULE && this.settings.xAxis === "day";
    const isNonPlanQuarterGranularity = this.module !== PLAN_MODULE && this.settings.xAxis === "quarter";

    const xAxis = (this.chart as XYChart).xAxes.push(
      DateAxis.new(this.root, {
        baseInterval: this.interval,
        groupData: false,
        strictMinMax: true,
        markUnitChange: true,
        renderer: this.renderers.x as AxisRendererX,
        dateFormats,
        periodChangeDateFormats: dateFormats,
        start: 0,
        end: 1,
        maxDeviation: 0.01,
      })
    );

    xAxis.get("renderer").labels.template.set("location", isMonth ? 0.5 : 0);
    xAxis.get("renderer").grid.template.set("location", isMonth ? 0.5 : 0);

    this.axes = { x: xAxis };

    const yPrimary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.yPrimary as AxisRendererY,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxisPrimary, this.data),
      })
    );

    const ySecondary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.ySecondary as AxisRendererY,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxisSecondary, this.data),
      })
    );

    if (isPlanLabelsGranularity || isNonPlanDayGranularity || isNonPlanQuarterGranularity) {
      const dateAxis = this.axes.x as DateAxis<AxisRendererX>;
      const yPrimaryAxis = yPrimary as ValueAxis<AxisRendererY>;
      const ySecondaryAxis = ySecondary as ValueAxis<AxisRendererY>;

      if (
        (isPlanLabelsGranularity && (this.settings.xAxis === "month" || this.settings.xAxis === "quarter")) ||
        isNonPlanQuarterGranularity
      ) {
        // For month/quarter with labels we need a small visual buffer at the edges,
        // but not a full extra bucket. DateAxis snaps to its own intervals, so
        // extraMin/extraMax is more stable here than trying to tweak max by a fraction of step.
        dateAxis.set("extraMin", 0.03);
        dateAxis.set("extraMax", this.settings.xAxis === "quarter" ? 0.01 : 0.02);
        if (isPlanLabelsGranularity) {
          yPrimaryAxis.set("extraMax", 0.05);
          ySecondaryAxis.set("extraMax", 0.05);
        }
      }

      this.root.events.once("frameended", () => {
        // Quarter auto-rounding can push the visible max to the next whole quarter,
        // which creates an empty extra division on the right. Clamp to real data max.
        if ((isPlanLabelsGranularity || isNonPlanQuarterGranularity) && this.settings.xAxis === "quarter") {
          const timestamps = this.data
            .map((item) => Number(item.timestamp))
            .filter((value) => Number.isFinite(value))
            .sort((a, b) => a - b);

          if (timestamps.length) {
            dateAxis.set("min", timestamps[0]);
            dateAxis.set("max", timestamps[timestamps.length - 1]);
            dateAxis.set("strictMinMax", true);
          }

          return;
        }

        const privateMin = dateAxis.getPrivate("min");
        const privateMax = dateAxis.getPrivate("max");
        const privateStep = dateAxis.getPrivate("step");
        if (
          typeof privateMin === "number" &&
          typeof privateMax === "number" &&
          typeof privateStep === "number" &&
          privateStep > 0
        ) {
          // Week and non-plan day need one real step of air on both sides so
          // bullets/labels do not hug the chart edge.
          if (this.settings.xAxis === "week" || isNonPlanDayGranularity) {
            dateAxis.set("min", privateMin - privateStep);
            dateAxis.set("max", privateMax + privateStep);
            dateAxis.set("strictMinMax", true);
          }
        }
      });

      if (isPlanLabelsGranularity) {
        this.root.events.once("frameended", () => {
          const primaryMin = yPrimaryAxis.getPrivate("min");
          const primaryMax = yPrimaryAxis.getPrivate("max");
          const primaryStep = yPrimaryAxis.getPrivate("step");
          if (
            typeof primaryMax === "number" &&
            typeof primaryStep === "number" &&
            primaryStep > 0 &&
            (this.settings.xAxis === "quarter" || this.settings.xAxis === "month") &&
            typeof primaryMin === "number"
          ) {
            yPrimaryAxis.set("min", primaryMin - primaryStep);
          }
          if (typeof primaryMax === "number" && typeof primaryStep === "number" && primaryStep > 0) {
            yPrimaryAxis.set("max", primaryMax + primaryStep);
            yPrimaryAxis.set("strictMinMax", true);
          }

          const secondaryMin = ySecondaryAxis.getPrivate("min");
          const secondaryMax = ySecondaryAxis.getPrivate("max");
          const secondaryStep = ySecondaryAxis.getPrivate("step");
          if (
            typeof secondaryMax === "number" &&
            typeof secondaryStep === "number" &&
            secondaryStep > 0 &&
            (this.settings.xAxis === "quarter" || this.settings.xAxis === "month") &&
            typeof secondaryMin === "number"
          ) {
            ySecondaryAxis.set("min", secondaryMin - secondaryStep);
          }
          if (typeof secondaryMax === "number" && typeof secondaryStep === "number" && secondaryStep > 0) {
            ySecondaryAxis.set("max", secondaryMax + secondaryStep);
            ySecondaryAxis.set("strictMinMax", true);
          }
        });
      }
    }

    if (this.settings.xAxisTitle) {
      this.axes.x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          centerX: p50,
          marginTop: axisTitle.marginTop,
        })
      );
    }

    if (this.settings.yAxisPrimaryTitle) {
      yPrimary.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisPrimaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    if (this.settings.yAxisSecondaryTitle) {
      ySecondary.children.push(
        Label.new(this.root, {
          text: this.settings.yAxisSecondaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: 90,
          x: p100,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { ...this.axes, yPrimary, ySecondary };
  }

  protected createCursor() {
    (this.chart as XYChart).set("cursor", XYCursor.new(this.root, { xAxis: this.axes.x }));
  }

  protected formatDate(timestamp: number) {
    const date = new Date(timestamp);
    return `${date.getUTCMonth() + 1}-${date.getUTCDate()}-${date.getUTCFullYear()}`;
  }

  protected createLineSeries(seriesName: string, index: number) {
    const isMonth = this.settings.xAxis === "month";
    const xLocation = isMonth ? 0.5 : 0;
    const seriesType = this.options.seriesType === "StepLineSeries" ? StepLineSeries : SmoothedXLineSeries;

    const series = (this.chart as XYChart).series.push(
      seriesType.new(this.root, {
        name: seriesName,
        xAxis: this.axes.x,
        yAxis: index ? this.axes.yPrimary : this.axes.ySecondary,
        valueYField: seriesName,
        valueXField: "timestamp",
        fill: this.getSeriesColor(index),
        stroke: this.getSeriesColor(index),
        minBulletDistance: 10,
        locationX: xLocation,
      })
    ) as LineSeries;

    series.strokes.template.setAll({
      strokeWidth: this.settings.lineThickness,
    });

    if (this.options.showBullets) {
      series.bullets.push(() => {
        const bulletCircle = Circle.new(this.root, {
          radius: 4,
          fill: series.get("fill"),
          strokeWidth: 2,
          stroke: series.get("stroke"),
        });

        return Bullet.new(this.root, {
          sprite: bulletCircle,
          locationX: xLocation,
        });
      });
    }

    const formatterConfig = this.getDataLabelsFormatterConfig();
    const metricKey = seriesName;
    const metricFormatter = this.formatters?.[metricKey];
    const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

    series.bullets.push(() => {
      const label = Label.new(this.root, {
        ...chartFontSettings.label,
        centerX: p50,
        centerY: p100,
        textAlign: "center",
        dy: -10,
        dx: 0,
        populateText: true,
        fontSize: labelFontSize,
        text: "{valueY}",
      });

      label.adapters.add("text", (labelText, target) => {
        const rawValue = Number(target?.dataItem?.get("valueY" as never));

        if (!Number.isFinite(rawValue) || rawValue === 0) {
          return "";
        }

        if (!this.settings.showDataLabels) {
          return "";
        }

        return metricFormatter
          ? metricFormatter(rawValue, formatterConfig)
          : this.root.numberFormatter.format(rawValue, this.getMetricFormatter(metricKey, formatterConfig));
      });

      label.adapters.add("visible", (visible) => {
        return this.settings.showDataLabels ? visible : false;
      });

      return Bullet.new(this.root, {
        locationX: xLocation,
        locationY: 1,
        sprite: label,
      });
    });

    this.createTooltip(series, (tooltipLabel) => {
      const param: { key: string; value: unknown }[] = [];

      const tooltipData = (tooltipLabel.dataItem?.dataContext ?? {}) as Record<string, string | number | Date>;

      if (!tooltipData) return [];

      const dateKey = this.settings.xAxis;
      const timestamp = tooltipData.timestamp as number | undefined;
      const raw = tooltipData[dateKey] as string | number | Date | undefined;
      param.push({
        key: dateKey,
        value: typeof timestamp === "number" && Number.isFinite(timestamp) ? timestamp : raw ?? null,
      });

      param.push({
        key: seriesName,
        value: tooltipData[seriesName],
      });

      return param;
    });

    this.createLegendLabel(series, seriesName);
    this.updateLineSeriesData(seriesName, series);
  }

  protected updateLineSeriesData(seriesName: string, series: LineSeries) {
    const data = this.data.map((el) => ({
      timestamp: el.timestamp,
      [this.settings.xAxis]: el[this.settings.xAxis],
      [seriesName]: el[seriesName],
    }));

    series.data.setAll(data);
  }
}
